'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Route } from 'next';
import { useCreateNote } from '@/hooks/useNotes';
import { toast } from '@/lib/toast';
import { createChordTracker, isPrimaryModifier, isTypingInInput } from '@/lib/shortcuts';
import { CommandPalette } from './CommandPalette';
import { COMMANDS } from './commands';

interface Props {
  children: React.ReactNode;
}

// Global host for the command palette + the chord-based shortcut
// handler. Mounted once at the (app) layout level so every protected
// page gets the shortcuts for free.
//
// Shortcuts wired here:
//   - Cmd/Ctrl+K → open the palette
//   - g s | g n | g a | g , → navigate (Linear-style two-key chord)
//
// Shortcuts inside an editing surface (input/textarea/contenteditable)
// are suppressed so typing stays uninterrupted.
export function CommandPaletteProvider({ children }: Props) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const createNoteMutation = useCreateNote();
  // Chord state lives in a ref so the keydown closure doesn't capture
  // a stale tracker between renders.
  const chordRef = useRef(createChordTracker(1000));

  const navigate = useCallback(
    (path: string) => {
      router.push(path as Route);
    },
    [router],
  );

  const closePalette = useCallback(() => setOpen(false), []);

  const createNote = useCallback(() => {
    createNoteMutation.mutate(undefined, {
      onSuccess: (note) => {
        navigate(`/notes?id=${note.id}`);
      },
      onError: () => toast.error("Couldn't create the note"),
    });
  }, [createNoteMutation, navigate]);

  const utils = useMemo(
    () => ({ navigate, closePalette, createNote }),
    [navigate, closePalette, createNote],
  );

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Cmd+K / Ctrl+K → toggle palette. Works even while typing
      // (this is the user's escape hatch into navigation from any
      // surface).
      if (isPrimaryModifier(e) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
        chordRef.current.disarm();
        return;
      }

      // Anything below this point is suppressed while typing OR while
      // the palette is open (palette has its own key handlers).
      if (open) return;
      if (isTypingInInput(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // `n` → focus the first task input on the current surface.
      // Surfaces opt in by setting data-shortcut-target="task-input"
      // on their form's <input>; the global handler just looks for the
      // first one in document order.
      if (e.key === 'n') {
        const target = document.querySelector<HTMLInputElement>(
          '[data-shortcut-target="task-input"]',
        );
        if (target) {
          e.preventDefault();
          target.focus();
          return;
        }
      }

      // "g _" chord — Linear-style navigation. Two presses, second
      // within 1s of the first.
      if (e.key === 'g') {
        chordRef.current.arm('g');
        return;
      }
      if (chordRef.current.peek() === 'g') {
        const target = chordToTarget(e.key);
        if (target) {
          e.preventDefault();
          chordRef.current.disarm();
          navigate(target);
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, open]);

  return (
    <>
      {children}
      <CommandPalette open={open} onOpenChange={setOpen} utils={utils} />
    </>
  );
}

// Maps the second key of a "g _" chord to its destination. Reads the
// COMMANDS table so adding a new "Go to X" command with a shortcut
// like "g x" just works — no separate dispatch table to keep in sync.
function chordToTarget(key: string): string | null {
  const lookFor = `g ${key}`;
  const cmd = COMMANDS.find((c) => c.shortcut === lookFor);
  if (!cmd) return null;
  // Only navigation commands belong in the chord table; "Create" group
  // wouldn't make sense via "g _".
  if (cmd.group !== 'Go to') return null;
  // Re-derive the path from the command's run function would require
  // injecting utils; cleaner to just inline the mapping.
  switch (cmd.id) {
    case 'go-schedule':
      return '/';
    case 'go-notes':
      return '/notes';
    case 'go-analytics':
      return '/analytics';
    case 'go-settings':
      return '/settings';
    default:
      return null;
  }
}
